<template>
    <div>
        <fullscreen ref="fullscreen" @change="fullscreenChange" background="#252f3f">
            <div id="app" class="font-sans antialiased bg-gray-800">
                <nav
                    id="header"
                    class="flex fixed w-full items-center justify-between px-4 md:px-6 h-12 md:h-16 bg-white text-gray-700 border-b border-gray-200 z-50">
                    <div class="flex items-center">
                        <a aria-label="Open Menu" class="hover:text-purple-600 focus:text-purple-700" @click="drawer">
                            <svg
                                class="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                viewBox="0 0 24 24"
                            >
                                <path d="M4 6h16M4 12h16M4 18h16"></path>
                            </svg>
                        </a>
                        <inertia-link :href="route('casino.home')" class="hidden md:block">
                            <img alt="Platform Light" class="h-auto w-40 mx-auto" src="/assets/logo.png"/>
                        </inertia-link>
                    </div>
                    <div class="flex items-center">
                        <div id="header-links" class="flex justify-between bg-transparent">
                            <switch-language
                                v-if="!$page.props.impersonator"
                                class=" px-3 py-1 hidden sm:block"
                            />
                            <div
                                v-if="$page.props.impersonator"
                                class="flex items-center px-3 py-1 font-medium ml-2"
                                :class="{
                                    'bg-danger': $page.props.impersonator,
                                    'text-white': $page.props.impersonator
                                }"
                            >
                                <span class="text font-semibold pr-1">{{ $page.props.impersonator }}</span>
                                <span class="text pr-1">{{ $t('impersonated as') }}</span>
                                <span class="text font-semibold" dusk="logged-user">{{ formattedUsername }}</span>
                            </div>
                            <inertia-link
                                v-else
                                id="header-username"
                                :href="route('user.changePassword')"
                                class="flex items-center px-2 md:px-4 py-1 font-medium ml-2 hover:text-purple-700 focus:text-purple-700 text-sm md:text-md"
                                title="Change password">
                                <font-awesome-icon icon="user" class="mr-2"/>
                                <span dusk="logged-user">{{ formattedUsername }}</span>
                            </inertia-link>
                            <balance id="header-balance" :balance="$page.props.balance" />
                            <a
                                @click="logout"
                                class="hidden sm:flex items-center px-2 py-1 font-medium ml-2 hover:text-purple-700 focus:text-purple-700"
                                :title="$t('Logout')"
                                dusk="logout-button">
                                <font-awesome-icon icon="power-off"/>
                            </a>
                        </div>
                    </div>

                    <transition
                        enter-active-class="ease-out transition-medium"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="ease-out transition-medium"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0">
                        <div v-show="isOpen" class="z-10 fixed inset-0 transition-opacity" @keydown.esc="isOpen = false">
                            <div class="absolute inset-0 bg-black opacity-50" tabindex="0" @click="isOpen = false"></div>
                        </div>
                    </transition>
                    <aside
                        :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
                        class="transform top-0 left-0 w-64 bg-white fixed h-full overflow-auto ease-in-out transition-all duration-300 z-30">
                        <inertia-link
                            :href="route('casino.home')"
                            :class="isRoute('casino.home') ? 'text-white bg-purple-600' : ''"
                            class="flex w-full items-center p-4 border-b"
                        >
                            <img alt="Platform Light" class="h-auto w-40" src="/assets/logo.png"/>
                        </inertia-link>
                        <inertia-link
                            :href="route('games.index')"
                            :class="isRoute('games.index') ? 'text-white bg-purple-600' : ''"
                            class="flex items-center p-4 hover:bg-purple-600 hover:text-white "
                            @click="isOpen = false">
                            <font-awesome-icon icon="dice-d6" class="mr-2"></font-awesome-icon>
                            <span>{{ $t('Games') }}</span>
                        </inertia-link>
                        <inertia-link
                            v-if="withdrawalPlayerWithPinAllowed && !externalId"
                            :href="route('withdraw.index')"
                            :class="isRoute('withdraw.index') ? 'text-white bg-purple-600' : ''"
                            class="flex items-center p-4 hover:bg-purple-600 hover:text-white "
                            @click="isOpen = false">
                            <font-awesome-icon icon="money-check" class="mr-2"></font-awesome-icon>

                            <span>{{ $t('Withdraw') }}</span>
                        </inertia-link>
                        <div class="fixed bottom-0 w-full border-t flex justify-between text-white bg-purple-700 sm:hidden">
                            <switch-language class="w-full px-5 py-3 hover:text-gray-300 focus:text-gray-300" />
                            <a
                                @click="logout"
                                class="flex bg-purple-800 items-center px-5 py-3 font-medium ml-2 hover:text-gray-300 focus:text-gray-300"
                                :title="$t('Logout')">
                                <font-awesome-icon icon="power-off"/>&nbsp;{{$t('Logout')}}
                            </a>
                        </div>
                    </aside>
                </nav>
                <main id="main">
                    <div class="relative p-3 md:p-4">
                        <a class="fullscreen-toggle" @click="toggle()" v-if="isGamesRoute() && allowFullscreen">
                            <font-awesome-icon v-if="!fullscreen" icon="expand" class="text-gray-300"/>
                            <font-awesome-icon v-if="fullscreen" icon="times" class="text-gray-300"/>
                        </a>
                        <slot></slot>
                    </div>
                </main>
                <flash-messages />
            </div>
        </fullscreen>
        <logoutModal v-if="playerHasOpenGameRound" ref="logoutModal" :openRoundGameId="openRoundGameId" @forceLogout="forceLogout()" @close="closeLogoutModal()"/>
    </div>
</template>

<script>
import Balance from './Balance';
import FlashMessages from '@/Shared/Components/FlashMessages';
import SwitchLanguage from '@/Shared/Components/SwitchLanguage';
import Fullscreen from "vue-fullscreen/src/component.vue"
import LogoutModal from "./Components/LogoutModal";

export default {
    components: {LogoutModal, Balance, FlashMessages, SwitchLanguage, Fullscreen },
    data() {
        return {
            isOpen: false,
            username: this.$page.props.user.username,
            externalId: this.$page.props.user.external_id,
            formattedUsername: '',
            fullscreen: false,
            allowFullscreen: false,
            withdrawalPlayerWithPinAllowed: window.withdrawal_player_with_pin,
            playerHasOpenGameRound: false,
            openRoundGameId: 0,
        };
    },
    methods: {
        drawer() {
            this.isOpen = !this.isOpen;
        },
        async logout() {
            await this.checkGameOpenRounds();
            if (!this.playerHasOpenGameRound) {
                await this.forceLogout();
            }
        },
        async forceLogout() {
            axios.post(this.route('logout').url()).then((response) => {
                if (response.data.url) {
                    window.location = response.data.url;
                } else {
                    window.location = '/';
                }
            })
        },
        closeLogoutModal() {
            this.playerHasOpenGameRound = false;
        },
        async checkGameOpenRounds() {
            const response = await axios.get(this.route('games.open-round'));
            if (response) {
                this.openRoundGameId = response.data.openRoundGameId;
                this.playerHasOpenGameRound = response.data.playerHasOpenGameRound;
            }
        },
        formatUsername() {
            const header = document.getElementById('header');
            if (header) {
                const usernameSpace = header.clientWidth - 112 - document.getElementById('header-balance').clientWidth;
                const chars = Math.floor(usernameSpace / 8);
                if (this.username.length > chars) {
                    this.formattedUsername = this.username.substring(0, chars) + '..';
                    return true;
                }
            }

            this.formattedUsername = this.username;
        },
        toggle () {
            this.$refs['fullscreen'].toggle();
        },
        fullscreenChange (fullscreen) {
            this.fullscreen = fullscreen
        },
        isGamesRoute() {
            return window.location.href === route('games.index').url();
        }
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(isOpen) {
                if (process.client) {
                    if (isOpen) document.body.style.setProperty("overflow", "hidden");
                    else document.body.style.removeProperty("overflow");
                }
            }
        }
    },
    mounted() {
        document.addEventListener("keydown", e => {
            if (e.keyCode == 27 && this.isOpen) this.isOpen = false;
        });
        this.formatUsername();
        this.allowFullscreen = this.$refs['fullscreen'].supportFullScreen;
    },
    created() {
        window.addEventListener("resize", this.formatUsername);

        if (this.$page.props.user.language !== this.$i18n.locale) {
            if (window.languages.length === 1) {
                this.$i18n.locale = window.languages[0];
            } else {
                this.$i18n.locale = this.$page.props.user.language;
            }
        }

        Echo
            .private(`player-${this.$page.props.user.id}`)
            .listen('ReloadPlayer', () => {
                console.log('reload');
                window.location.reload();
            });
    },
    destroyed() {
        window.removeEventListener("resize", this.formatUsername);
    },
};
</script>
<style lang="scss">
aside {
    a {
        cursor: pointer;
    }
}

#main {
    padding-top: 48px;

    @media (min-width: 768px) {
        padding-top: 64px;
    }
}

a {
    cursor: pointer;
}

.fullscreen-toggle {
    position: absolute;
    right: 8px;
    top: 6px;
    padding: 5px;
    z-index: 15;

    @media (min-width: 768px) {
        right: 10px;
        top: 10px;
    }
}
</style>
